import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Map } from 'immutable'
import moment from 'moment'
import xtend from 'xtend'
import _ from 'underscore'

import Switch from 'react-bootstrap-switch'
import DatePicker from 'react-datepicker'
import ClockPicker from '../../../../common/components/inputs/ClockPicker'

import withValidator from '../../../../hocs/Validator'

import SentryReporter from '../../../../utils/SentryReporter'
import LargeToolTip from '../../../../account/components/LargeToolTip';
import InfoIcon from '@material-ui/icons/Info';


const DATE_FMT = 'YYYY-MM-DD'
const TIME_FMT = 'HH:mm:ss'
const FULL_DT_FMT = [DATE_FMT, TIME_FMT].join(' ')

const AutomationInputSchema = [{
    label: 'Auto Start',
    name: 'autoStart',
    type: 'switch'
},
{
    label: 'Auto Stop',
    name: 'autoStop',
    type: 'switch'
}, 
{
    label: 'Go To Break After Start',
    name: 'liveToBreak',
    type: 'switch',
    tooltipContent: "When starting a live event, this feature will automatically set the event to 'On Break' right after the event starts"
},
{
    label: <span>Preroll <small>(minutes)</small></span>,
    name: 'preroll',
    type: 'select'
}, {
    label: 'Stop Date',
    name: 'stopDate',
    type: 'dateSelect'
}, {
    label: 'Stop Time',
    name: 'stopTime',
    type: 'timeSelect'
}, {
    label: 'Duration',
    name: 'duration',
    type: 'duration'
}]

/**
 * Check if event is Immutable.Map isntance 
 * otherwise assume it's an Object
 * 
 * @param {Object} props component props
 * @returns {Object} The Event in Object form
 */
function getEvent (props) {
    let { event } = props
    return Map.isMap(event) ?
        event.toJS() : event;
}

/**
 * @param {Integer} i Preroll value
 * @returns {String}
 */
function getPrerollName (i) {
    return (i == 0) ?
        'None' : 
        [
            i, ((i == 1) ? 'minute' : 'minutes')
        ].join(' ')
}

function calculateDuration (stopDate, stopTime, startDateTime, preroll) {
    if (!stopDate || !stopTime || !startDateTime) return 'Not Available'

    let autoStopTime = moment(
        [
            stopDate.format(DATE_FMT), 
            stopTime.format(TIME_FMT)
        ].join(' '),
        FULL_DT_FMT
    )

    let diff = autoStopTime.diff(startDateTime)

    if (preroll != 0) {
        // diff is in milliseconds,
        // preroll is in minutes (integer)
        diff = diff + (60000 * preroll)
    }
    
    let duration = moment.duration(diff)

    return (
        Math.floor(duration.asHours()) + 
        moment.utc(diff).format(":mm:ss") || 
            'Not Available'
    )
}

class AutomationTab extends Component {
    constructor (props) {
        super(props)

        this.renderInput = this.renderInput.bind(this)
        this.getEditState = this.getEditState.bind(this)
        this.handleChange = this.handleChange.bind(this)
        this.handleDateTimeChange = this.handleDateTimeChange.bind(this)
    }

    shouldComponentUpdate (nextProps, nextState, nextContext) {
        return (
            this.props.edits != nextProps.edits ||
            !this.props.event.equals(nextProps.event.toJS())
        );
    }

    getEditState () {
        let event = getEvent(this.props)
        let startDateTime = moment(event.startDateTime, FULL_DT_FMT)

        let stateMembers = _.pick(event, 
            'autoStart',
            'autoStop',
            'liveToBreak',
            'preroll',
            'autoStopTime',
            'stopDate',
            'stopTime'
        )
        
        return {
            event: event,
            startDateTime: startDateTime,
            stateMembers: xtend(stateMembers, this.props.edits)
        }
    }

    handleChange (name, event) {
        let value = event.target.value;
        let updatedProperty = {}
        updatedProperty[event.target.name] = value

        this.props.handleChange(event.target.name, value);
    }

    handleDateTimeChange (name, value) {
        if (value !== null) {
            this.handleChange(name, {
                target: {
                    name: name,
                    type: 'text',
                    value: value
                }
            });
        }
    }

    render () {
        let edits = this.getEditState().stateMembers
        var clientTZ = moment.tz(this.props.timeZone)
        var clientTZabbr = clientTZ.format("z");
        var offset = clientTZ.utcOffset() / 60;
        return <div className="tab-automation">
            <div className="callout clearfix">
                {AutomationInputSchema.map((el, k) => {
                    return <div key={k} className="col-md-4">
                        <label>{el.label}</label>
                        {
                            el.tooltipContent && 
                                <LargeToolTip title={el.tooltipContent}>
                                    <InfoIcon style={{color:'#5252c6',fontSize:20,marginBottom:'-5px',marginLeft:'5px'}}/>
                                </LargeToolTip>
                        }

                        {(el.name === 'stopTime') ? <span className='tz-notate'>{' ('+clientTZabbr+', UTC '+offset+')'} </span>: ''}
                        {this.renderInput(el, edits)}
                    </div>
                })}
            </div>
        </div>
    }

    renderInput (input, edits)  {

        let component = null

        switch (input.type) {
            case 'switch':
                let value = !!edits[input.name]
                component = <Switch
                    value={value}
                    animate={false}
                    onChange={(element, value) => this.handleChange(input.name, {
                        target: {
                            name: input.name,
                            type: 'checkbox',
                            value: value
                        }
                    })}/>;
                break;

            case 'select':

                let preroll = edits.preroll
                let prerollOptions = [
                    <option value={preroll} key={'preroll-selected'}>
                        {getPrerollName(preroll)}
                    </option>,
                    <option value={0} key={'preroll-empty'}>&mdash;</option>
                ];
        
                for (let i = 0; i <= 30; i++) {
                    prerollOptions.push(
                        <option key={'preroll-' + i} value={i}>
                            {getPrerollName(i)}
                        </option>
                    )
                }

                component = <select
                    className="form-control"
                    name="preroll"
                    id="preroll"
                    onChange={this.handleChange.bind(null, input.name)}>
                        {prerollOptions}
                </select>
                break;

            case 'dateSelect':
                component = <div className="input-group">
                    <span className="input-group-addon">
                        <i className="fa fa-calendar"/>
                    </span>
                    <DatePicker
                        disabled={!!edits.autoStop === false}
                        className="form-control"
                        dateFormat="MM/DD/YYYY"
                        selected={edits.stopDate || null}
                        placeholderText="Required"
                        onChange={this.handleDateTimeChange.bind(null, input.name)}/>
                </div>
                break;

            case 'timeSelect':
                component = <div className="input-group">
                    <span className="input-group-addon"><i className="far fa-clock"/></span>
                    <ClockPicker
                        disabled={!!edits.autoStop === false}
                        name="stopTime"
                        value={edits.stopTime || null}
                        id={this.props.event.get('eventID') + '-stopTime-input'}
                        handleChange={this.handleDateTimeChange.bind(null, input.name)}/>
                </div>
                break;

            case 'duration':
                component = <input
                    type="text"
                    className="form-control"
                    value={calculateDuration(
                        edits.stopDate,
                        edits.stopTime,
                        edits.startDateTime,
                        edits.preroll
                    )}
                    placeholder="Duration"
                    disabled={true}
                    onChange={null}/>
                break;
        }
        
        let hasValidationErr = false

        if (this.props.hasError !== false) {
            this.props.hasError.map(v => {
                if (v.key === input.name) hasValidationErr = true
            })
        }

        // if(input.tooltipContent) {
        //     console.log("tooltip")
        //     component = <LargeToolTip title={input.tooltipContent}>{(<>{component}</>}</LargeToolTip>
        //     console.log(component)
        // }

        return <div style={{display: 'block'}}>
            <div className={[
                'validation-wrapper',
                (hasValidationErr ? 'invalid' : 'valid')
            ].join(' ')}>
                {component}
            </div>
        </div>
    }
}

AutomationTab.propTypes = {
    event: PropTypes.object.isRequired,
    actions: PropTypes.shape({
        edit: PropTypes.func.isRequired
    }).isRequired
}

export default withValidator(AutomationTab)